/**
 * useMetaInfoRemoveConfirm — thin caller around the global confirm-dialog
 * store for removing loose-FK or JSON-column declarations from the Settings
 * → Meta Info view. Wraps the remove IPC calls, query invalidation, JSON
 * orphan-filter cleanup, and analytics; opens the app-level ConfirmDialog
 * via `openConfirmDialog` instead of rendering a local AlertDialog (which
 * would be nested inside the Settings Dialog and leave `body
 * { pointer-events: none }` stuck on close).
 */

import { useAnalytics } from '@/core/analytics/useAnalytics';
import { toast } from '@/components/ui/toaster';
import { openConfirmDialog } from '../../../../workspace/common/components/confirm/confirmDialogStore';
import {
  removeEnumColumn,
  removeJsonColumn,
  removeLooseRelationship,
  removeSpatialColumn,
} from '../../../../workspace/common/services/metaInfoApi';
import { useInvalidateMetaInfo } from '../../../../workspace/common/hooks/useMetaInfo';
import { useWorkspaceStore } from '../../../../workspace/core/store/workspaceStore';
import type {
  EnumColumnDeclaration,
  JsonColumnDeclaration,
  LooseRelationship,
  SpatialColumnDeclaration,
} from '@/types/electron';

export type MetaInfoRemoveConfirm = {
  requestRemoveLoose: (
    connectionId: string,
    database: string,
    rel: LooseRelationship,
    label: string,
  ) => void;
  requestRemoveJson: (
    connectionId: string,
    database: string,
    decl: JsonColumnDeclaration,
  ) => void;
  requestRemoveEnum: (
    connectionId: string,
    database: string,
    decl: EnumColumnDeclaration,
  ) => void;
  requestRemoveSpatial: (
    connectionId: string,
    database: string,
    decl: SpatialColumnDeclaration,
  ) => void;
};

export function useMetaInfoRemoveConfirm(): MetaInfoRemoveConfirm {
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();

  const requestRemoveLoose: MetaInfoRemoveConfirm['requestRemoveLoose'] = (
    connectionId,
    database,
    rel,
    label,
  ) => {
    openConfirmDialog({
      title: 'Remove loose relationship?',
      description: `Remove the loose-FK declaration for ${label}? This won't delete data — only the relationship.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeLooseRelationship(connectionId, database, rel.id);
        await invalidate(connectionId, database);
        track('feature:loose_fk_removed', { source: 'settings' });
      },
    });
  };

  const requestRemoveJson: MetaInfoRemoveConfirm['requestRemoveJson'] = (
    connectionId,
    database,
    decl,
  ) => {
    const { id, schema, table, column } = decl;
    openConfirmDialog({
      title: 'Remove JSON-column declaration?',
      description: `Remove the JSON-column declaration for ${schema}.${table}.${column}? Filters and the tree-view cell renderer will fall back to plain-text behavior.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeJsonColumn(connectionId, database, id);
        await invalidate(connectionId, database);
        const removed = useWorkspaceStore
          .getState()
          .removeJsonOrphanFilters({ connectionId, database, schema, table, column });
        if (removed > 0) {
          toast.success({
            title: `Removed ${removed} JSON filter${removed === 1 ? '' : 's'} on ${column}`,
          });
        }
        track('feature:json_undeclare', { source: 'settings' });
      },
    });
  };

  const requestRemoveEnum: MetaInfoRemoveConfirm['requestRemoveEnum'] = (
    connectionId,
    database,
    decl,
  ) => {
    const { id, schema, table, column, path } = decl;
    const isPath = !!path;
    const label = `${schema}.${table}.${column}${path ? ` · ${path}` : ''}`;
    openConfirmDialog({
      title: isPath
        ? 'Remove JSON-path enum declaration?'
        : 'Remove enum-column declaration?',
      description: isPath
        ? `Remove the JSON-path enum declaration for ${label}? Filters on this path will fall back to a free-text input.`
        : `Remove the enum-column declaration for ${label}? Filters on this column will fall back to a free-text input.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeEnumColumn(connectionId, database, id);
        await invalidate(connectionId, database);
        track('feature:enum_undeclare', { source: 'settings', on_json_path: isPath });
      },
    });
  };

  const requestRemoveSpatial: MetaInfoRemoveConfirm['requestRemoveSpatial'] = (
    connectionId,
    database,
    decl,
  ) => {
    const { id, schema, table, column } = decl;
    openConfirmDialog({
      title: 'Remove coordinate-column declaration?',
      description: `Remove the coordinate declaration for ${schema}.${table}.${column}? The column will no longer render on the map.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeSpatialColumn(connectionId, database, id);
        await invalidate(connectionId, database);
        track('feature:spatial_undeclare', { source: 'settings' });
      },
    });
  };

  return {
    requestRemoveLoose,
    requestRemoveJson,
    requestRemoveEnum,
    requestRemoveSpatial,
  };
}
